import { FC, useState, useContext } from 'react';
import { Icon, DateDisplay, useToast, ActionsDropdown, Badge } from '@aidkitorg/component-library';
import { safeParse } from '../../../../../Util';
import { useLocalizedStrings } from '../../../../../Localization';
import { usePost } from '../../../../../API';
import InterfaceContext, { ConfigurationContext, UserInfoContext } from '../../../../../Context';
import { MessageBubbleProps, HideMessageReason } from './shared/types';
import { useFirstSignedURL, useSignedURLs, useCopyToClipboard, useDeleteMessage, useDeleteAttachments, useDeleteRecording } from './shared/hooks';
import { generateHiddenMessage, determineApplicantIdentity } from './shared/utils';
import { statusColors, getStatusMap } from './shared/constants';
import { MessageContent } from './components/MessageContent';
import { DebugMessageModal, HideMessageModal, DeleteAttachmentsModal, DeleteRecordingModal, DeleteMessageModal } from './components/modals';

export const MessageBubble: FC<MessageBubbleProps> = ({ message, applicantInfo }) => {
  const L = useLocalizedStrings();
  const toast = useToast();
  const config = useContext(ConfigurationContext);
  const context = useContext(InterfaceContext);
  const userContext = useContext(UserInfoContext);
  const metadata = safeParse(message.metadata || '{}');

  // Check if the message is hidden/flagged
  const hiddenMessage = generateHiddenMessage(message, L);

  // State for debug and hide message modals
  const [showDebugMessage, setShowDebugMessage] = useState<any | null>(null);
  const [showHideMessage, setShowHideMessage] = useState<any | null>(null);
  const [hideMessageReason, setHideMessageReason] = useState<HideMessageReason | ''>('');
  const [hideMessageDetails, setHideMessageDetails] = useState<string>('');
  const [shouldHideAttachmentOnly, setShouldHideAttachmentOnly] = useState(false);
  const [showDeleteAttachments, setShowDeleteAttachments] = useState<boolean>(false);
  const [deleteAttachmentsReason, setDeleteAttachmentsReason] = useState<string>('');
  const [showDeleteRecording, setShowDeleteRecording] = useState<boolean>(false);
  const [deleteRecordingReason, setDeleteRecordingReason] = useState<string>('');
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [deleteMessageReason, setDeleteMessageReason] = useState<string>('');

  // API hooks
  const getMessageDebugInfo = usePost('/admin/message_details');
  const hideMessage = usePost('/messages/update_visibility');

  // Use extracted mutation hooks
  const deleteMessageMutation = useDeleteMessage();
  const deleteAttachmentsMutation = useDeleteAttachments();
  const deleteRecordingMutation = useDeleteRecording();

  // Get applicant selfie for avatar
  const Selfie = useFirstSignedURL(applicantInfo?.['selfie']);

  // Get attachments from metadata
  const attachments = metadata.attachments || [];
  const attachmentTypes = metadata.attachmentTypes || [];
  const signedURLs = useSignedURLs(attachments);

  // Parse scheduled send time from metadata
  const scheduledSendTime = metadata.sendAt ? new Date(metadata.sendAt) : null;
  const displayTime = scheduledSendTime || new Date(message.created_at);
  const timestampPrefix = scheduledSendTime ? L.applicant.comms.for + ' ' : '';

  // Copy to clipboard hook
  const { copied, handleCopyText } = useCopyToClipboard(message.message || '');

  // Determine direction: inbound if needs_attention or has certain metadata
  const direction = message.needs_attention || metadata.handled_by || metadata.ToCountry || metadata.ApiVersion || metadata.eventSource === "aws:ses" || metadata.isInboundVoiceRecordingDisabled
    ? 'inbound'
    : 'outbound';

  // Parse alternate contacts from config and determine applicant identity
  const alternateContacts = safeParse(config.alternate_contact || '[]', []);

  // Determine applicant identity using utility function
  const { name: applicantName, label: applicantLabel, source: applicantSource } =
    determineApplicantIdentity(message, applicantInfo, alternateContacts, direction, L, context);

  const author = direction === 'inbound'
    ? (applicantName || message.source || L.applicant.comms.unknown_sender)
    : (message.author_name || 'System');

  // Permission checks for actions dropdown
  const canDebugMessages = (userContext?.tags ?? []).some((tag: string) => ['admin'].includes(tag));
  const canManageMessages = (userContext?.tags ?? []).some((tag: string) => ['admin', 'comms admin'].includes(tag));

  // Handlers for actions dropdown
  const handleDebugMessage = async () => {
    try {
      const response = await getMessageDebugInfo({
        id: parseInt(message.id + '')
      });
      setShowDebugMessage(response);
    } catch (error) {
      toast.toast({ variant: 'error', title: L.applicant.comms.failed_to_fetch_message_details });
    }
  };

  const handleHideMessage = async () => {
    try {
      const response = await getMessageDebugInfo({
        id: parseInt(message.id + '')
      });
      setShowHideMessage(response);
    } catch (error) {
      toast.toast({ variant: 'error', title: L.applicant.comms.failed_to_fetch_message_details });
    }
  };

  const handleDeleteMessage = () => {
    setShowConfirmDelete(true);
  };

  const confirmDeleteMessage = () => {
    if (deleteMessageReason.trim()) {
      deleteMessageMutation.mutate(
        {
          messageId: String(message.id),
          reason: deleteMessageReason,
        },
        {
          onSuccess: () => {
            clearDeleteMessageModal();
          }
        }
      );
    }
  };

  const clearDeleteMessageModal = () => {
    setShowConfirmDelete(false);
    setDeleteMessageReason('');
  };

  const clearDebugMessageModal = () => {
    setShowDebugMessage(null);
  };

  const clearHideMessageModal = () => {
    setShowHideMessage(null);
    setHideMessageReason('');
    setHideMessageDetails('');
    setShouldHideAttachmentOnly(false);
  };

  const clearDeleteAttachmentsModal = () => {
    setShowDeleteAttachments(false);
    setDeleteAttachmentsReason('');
  };

  const handleDeleteAttachments = () => {
    setShowDeleteAttachments(true);
  };

  const confirmDeleteAttachments = () => {
    if (deleteAttachmentsReason.trim()) {
      deleteAttachmentsMutation.mutate(
        {
          messageId: String(message.id),
          reason: deleteAttachmentsReason,
        },
        {
          onSuccess: () => {
            clearDeleteAttachmentsModal();
          }
        }
      );
    }
  };

  const clearDeleteRecordingModal = () => {
    setShowDeleteRecording(false);
    setDeleteRecordingReason('');
  };

  const handleDeleteRecording = () => {
    setShowDeleteRecording(true);
  };

  const confirmDeleteRecording = () => {
    if (deleteRecordingReason.trim()) {
      deleteRecordingMutation.mutate(
        {
          messageId: String(message.id),
          reason: deleteRecordingReason,
        },
        {
          onSuccess: () => {
            clearDeleteRecordingModal();
          }
        }
      );
    }
  };

  const confirmHideMessage = async () => {
    if (showHideMessage?.message?.id && hideMessageReason) {
      try {
        await hideMessage({
          messageIds: [showHideMessage.message.id],
          action: 'hide',
          metadata: {
            reason: hideMessageReason,
            ...(hideMessageDetails && { details: hideMessageDetails }),
            ...(shouldHideAttachmentOnly && { attachmentOnly: shouldHideAttachmentOnly }),
          }
        });
        clearHideMessageModal();
        toast.toast({ description: L.applicant.comms.message_hidden, variant: 'success' });
      } catch (error) {
        toast.toast({ variant: 'error', title: L.applicant.comms.failed_to_hide_message });
      }
    }
  };

  // Status badge for messages
  const getStatusBadge = () => {
    if (direction === 'inbound' && message.needs_attention) {
      return (
        <Badge className="text-xs" variant="warning">
          {L.applicant.comms.new_unhandled}
        </Badge>
      );
    }
    if (message.status) {
      const statusMap = getStatusMap(L);
      const statusVariant = statusColors[message.status] || "neutral";
      const statusLabel = statusMap[message.status] || message.status.charAt(0).toUpperCase() + message.status.slice(1);
      return (
        <Badge className="text-xs" variant={statusVariant}>
          {statusLabel}
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="relative">
      <div className="relative flex gap-3">
        {/* Avatar */}
        <div
          className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center overflow-hidden ${
            author === "System" ? "bg-gray-300" : direction === "inbound" ? "bg-gray-300" : "bg-blue-600"
          }`}
        >
          {author === "System" ? (
            <img src="/aidkit-social-black.png" alt={L.applicant.comms.aidkit_alt_text} className="w-full h-full object-cover" />
          ) : direction === "inbound" && Selfie ? (
            <img src={Selfie} alt={L.applicant.applicant} className="w-full h-full object-cover" />
          ) : author.startsWith("+") ? (
            <Icon name="UserIcon" className="h-5 w-5 text-white" />
          ) : (
            <span className="text-white font-medium text-sm">{author.charAt(0).toUpperCase()}</span>
          )}
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-sm font-semibold text-gray-900">
              {author === "System" ? L.applicant.comms.system : author}
            </span>
            {(message.kind === "sms" || message.kind === "mms") && (
              <Badge className="text-xs">
                {L.applicant.comms.via_sms}
              </Badge>
            )}
            {message.kind === "email" && (
              <Badge className="text-xs">
                {L.applicant.comms.via_email}
              </Badge>
            )}
            {message.kind === "whatsapp" && (
              <Badge className="text-xs">
                {L.applicant.comms.via_whatsapp}
              </Badge>
            )}
            {getStatusBadge()}
            {!message.platform_message_id && (
              <Badge className="text-xs" variant="error">                
                {L.applicant.comms.message_failed}
              </Badge>
            )}
            <span className="text-xs text-gray-500">
              {timestampPrefix}
              <DateDisplay
                utcValue={displayTime}
                variant="datetime"
                displayInUTC={false}
                options={{
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                }}
                className="!text-xs !text-gray-500"
              />
            </span>
            {(canDebugMessages || canManageMessages) && (
              <ActionsDropdown
                item={message}
                actions={[
                  ...(canDebugMessages ? [{
                    label: 'Debug Message',
                    onClick: handleDebugMessage,
                    icon: 'BugAntIcon' as const,
                  }] : []),
                  ...(canManageMessages ? [{
                    label: 'Hide Message',
                    onClick: handleHideMessage,
                    icon: 'EyeSlashIcon' as const,
                  }] : []),
                  ...(canManageMessages && attachments.length > 0 ? [{
                    label: 'Delete Attachments',
                    onClick: handleDeleteAttachments,
                    icon: 'TrashIcon' as const,
                  }] : []),
                  ...(canManageMessages && metadata.RecordingUrl ? [{
                    label: 'Delete Recording',
                    onClick: handleDeleteRecording,
                    icon: 'TrashIcon' as const,
                  }] : []),
                  ...(canManageMessages ? [{
                    label: 'Delete Message',
                    onClick: handleDeleteMessage,
                    icon: 'TrashIcon' as const,
                  }] : []),
                ]}
                triggerIcon={<Icon size="sm" name="EllipsisVerticalIcon" type="solid" />}
                className="ml-auto -my-1 p-0 leading-none flex items-center"
              />
            )}
          </div>

          {/* SMS/MMS/WhatsApp subtitle and details */}
          {(message.kind === 'sms' || message.kind === 'mms' || message.kind === 'whatsapp') && (
            <div className="mb-2">
              <div className="text-xs text-gray-500">
                {message.kind === 'whatsapp'
                  ? (direction === 'inbound' ? L.applicant.comms.inbound_whatsapp : L.applicant.comms.outbound_whatsapp)
                  : (direction === 'inbound' ? L.applicant.comms.inbound_sms : L.applicant.comms.outbound_sms)
                }
              </div>
              <div className="text-xs text-gray-500">
                {L.applicant.comms.from.replace('$from', message.source)} → {L.applicant.comms.to.replace('$to', message.destination)}
              </div>
              {!message.needs_attention && metadata && metadata['handled_by'] && metadata['handled_date'] && (
                <div className="text-xs text-gray-500">
                  <em>
                    {L.applicant.comms.handled_by} {metadata['handled_by']}{' '}
                    <DateDisplay
                      utcValue={(metadata['handled_date'] || '').toString().indexOf('Z') !== -1 ? new Date(metadata['handled_date']) : new Date(metadata['handled_date'] * 1000)}
                      variant="datetime"
                      displayInUTC={false}
                      options={{
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      }}
                      className="!text-xs !text-gray-500"
                    />
                  </em>
                </div>
              )}
            </div>
          )}

          {/* Email subtitle and details */}
          {message.kind === 'email' && (
            <div className="mb-2">
              <div className="text-xs text-gray-500 mb-1">
                {direction === 'inbound' ? L.applicant.comms.inbound_email : L.applicant.comms.outbound_email}
              </div>
              <div className="text-xs text-gray-500">
                {L.applicant.comms.from.replace('$from', message.source)} → {L.applicant.comms.to.replace('$to', message.destination)}
              </div>
              {!message.needs_attention && metadata && metadata['handled_by'] && metadata['handled_date'] && (
                <div className="text-xs text-gray-500">
                  <em>
                    {L.applicant.comms.handled_by} {metadata['handled_by']}{' '}
                    <DateDisplay
                      utcValue={(metadata['handled_date'] || '').toString().indexOf('Z') !== -1 ? new Date(metadata['handled_date']) : new Date(metadata['handled_date'] * 1000)}
                      variant="datetime"
                      displayInUTC={false}
                      options={{
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      }}
                      className="!text-xs !text-gray-500"
                    />
                  </em>
                </div>
              )}
            </div>
          )}

          {/* Call subtitle and details */}
          {message.kind === 'call' && (
            <div className="mb-2">
              <div className="text-xs text-gray-500">
                {direction === 'outbound' ? L.applicant.comms.outbound_call_short : L.applicant.comms.inbound_call_short}
              </div>
              <div className="text-xs text-gray-500">
                {L.applicant.comms.from.replace('$from', message.source)} → {L.applicant.comms.to.replace('$to', message.destination)}
              </div>
              {!message.needs_attention && metadata && metadata['handled_by'] && metadata['handled_date'] && (
                <div className="text-xs text-gray-500">
                  <em>
                    {L.applicant.comms.handled_by} {metadata['handled_by']}{' '}
                    <DateDisplay
                      utcValue={(metadata['handled_date'] || '').toString().indexOf('Z') !== -1 ? new Date(metadata['handled_date']) : new Date(metadata['handled_date'] * 1000)}
                      variant="datetime"
                      displayInUTC={false}
                      options={{
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      }}
                      className="!text-xs !text-gray-500"
                    />
                  </em>
                </div>
              )}
            </div>
          )}

          {/* Alternate Contact Indicator */}
          {applicantInfo &&
            (applicantSource !== "current" ||
              (metadata.onBehalfOfApplicant && metadata.onBehalfOfApplicant !== applicantInfo?.uid)) && (
            <div className="mb-2 text-xs text-gray-500 flex items-center gap-1">
              <Icon name="InformationCircleIcon" size="sm" className="text-gray-400" strokeWidth={1.5} />
              {metadata.onBehalfOfApplicant ? (
                metadata.onBehalfOfApplicant === applicantInfo?.uid ? (
                  <span>
                    {L.applicant.comms.sent_to_applicant} {applicantInfo?.["legal_name"] || "the applicant"}'s {applicantLabel}
                  </span>
                ) : (
                  <span>{L.applicant.comms.sent_on_behalf_of_another}</span>
                )
              ) : (
                <span>{applicantLabel}</span>
              )}
            </div>
          )}

          {/* Message Kind-Specific Rendering */}
          <MessageContent
            message={message}
            metadata={metadata}
            hiddenMessage={hiddenMessage}
            attachments={attachments}
            attachmentTypes={attachmentTypes}
            signedURLs={signedURLs}
            direction={direction}
            copied={copied}
            onCopyText={handleCopyText}
            applicantInfo={applicantInfo}
          />
        </div>
      </div>

      {/* Modals */}
      <DebugMessageModal
        showDebugMessage={showDebugMessage}
        onClose={clearDebugMessageModal}
      />

      <HideMessageModal
        showHideMessage={showHideMessage}
        hideMessageReason={hideMessageReason}
        hideMessageDetails={hideMessageDetails}
        shouldHideAttachmentOnly={shouldHideAttachmentOnly}
        onClose={clearHideMessageModal}
        onReasonChange={setHideMessageReason}
        onDetailsChange={setHideMessageDetails}
        onAttachmentOnlyChange={() => setShouldHideAttachmentOnly(!shouldHideAttachmentOnly)}
        onConfirm={confirmHideMessage}
      />

      <DeleteAttachmentsModal
        showDeleteAttachments={showDeleteAttachments}
        deleteAttachmentsReason={deleteAttachmentsReason}
        onClose={clearDeleteAttachmentsModal}
        onReasonChange={setDeleteAttachmentsReason}
        onConfirm={confirmDeleteAttachments}
      />

      <DeleteRecordingModal
        showDeleteRecording={showDeleteRecording}
        deleteRecordingReason={deleteRecordingReason}
        onClose={clearDeleteRecordingModal}
        onReasonChange={setDeleteRecordingReason}
        onConfirm={confirmDeleteRecording}
      />

      <DeleteMessageModal
        isOpen={showConfirmDelete}
        deleteMessageReason={deleteMessageReason}
        onClose={clearDeleteMessageModal}
        onReasonChange={setDeleteMessageReason}
        onConfirm={confirmDeleteMessage}
      />
    </div>
  );
};
